/**
 * Lykke for clients, app-wide: the conversations (the home one and one per campaign) live above the routes, so a
 * reply that moves the app is not lost when the page changes: Lykke opens the page, and her line lands in the dock
 * on the new page (or in the hero when that page is Home).
 *
 * One provider: conversation state per scope ('home' or a campaign id), the dock (open/closed + which scope), voice
 * (browser speech recognition in, Lykke's voice out), and navigation (lykkePath → react-router).
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { lykkeApi, lykkeTurn, type DecisionCardData, type LykkeFileIn, type LykkeMessage, type RequestEvent } from './lykkeApi';
import { lykkePagesFor, lykkePath } from './lykkePages';
import { pathFor, type Viewer } from '../guide/pageMap';
import { coworkApi } from '@/cowork/omniad/api/coworkApi';
import { RealtimeEars } from '@/cowork/proposals/realtimeCall';
import { deckAsk, giveHandoff, isBriefFile } from '@/cowork/shell/handoff';

export type Scope = string; // 'home' or a campaign id
export const HOME: Scope = 'home';

export interface ChatItem {
  id: string;
  role: 'user' | 'assistant' | 'nav' | 'error';
  text: string;
  via?: 'voice';
  /** Names of the files the person attached. */
  files?: string[];
  decision?: DecisionCardData;
  request?: RequestEvent;
  /** A line from a person at Beatly (an answer to a question Lykke passed on). */
  fromBeatly?: boolean;
  pending?: boolean;
  /** A line from the app, not from Lykke (a file she cannot read here). */
  note?: boolean;
}

export type VoiceState = 'off' | 'connecting' | 'listening' | 'thinking' | 'speaking';

/** Where the person is when they ask (shell/pageContext.tsx): sent as `here` so Lykke knows the page and its object. */
export interface LykkeHere { page: string; label?: string; path?: string }

interface LykkeCtx {
  available: boolean;
  items: (scope: Scope) => ChatItem[];
  loaded: (scope: Scope) => boolean;
  busy: (scope: Scope) => boolean;
  working: (scope: Scope) => string | null;
  /** The campaign's name for a campaign conversation (once loaded). */
  title: (scope: Scope) => string | null;
  load: (scope: Scope) => Promise<void>;
  /** `raw`: the same files as File objects, for a page Lykke opens that reads them itself (a brief to Order a campaign). */
  send: (scope: Scope, text: string, via?: 'voice', files?: LykkeFileIn[], raw?: File[]) => Promise<void>;
  /** A line from the app in the conversation (not saved, not from Lykke). */
  note: (scope: Scope, text: string) => void;
  dock: { open: boolean; scope: Scope };
  openDock: (scope: Scope) => void;
  closeDock: () => void;
  voice: VoiceState;
  voiceScope: Scope | null;
  voiceSupported: boolean;
  startVoice: (scope: Scope) => void;
  stopVoice: () => void;
  /** Mute the microphone during a call (she keeps talking). */
  muted: boolean;
  setMuted: (m: boolean) => void;
  /** What she is saying or what was heard on a call, for the call bar. */
  caption: string;
}

const Ctx = createContext<LykkeCtx | null>(null);
// eslint-disable-next-line react-refresh/only-export-components -- the hook belongs with its provider
export const useLykke = () => useContext(Ctx);

const WORKING: Record<string, string> = {
  find_campaigns: 'Looking at your campaigns', campaign_status: 'Reading the campaign', creators: 'Checking the creators', drafts: 'Checking the drafts',
  publications: 'Reading the publications', results: 'Adding up the results', payments: 'Checking payments', needs_you: 'Checking what needs you',
  calendar: 'Reading the calendar', brand: 'Reading Brand OS', content: 'Looking in the Content Hub', dashboards: 'Looking at your dashboards',
  show_decision: 'Getting it ready for you', ask_beatly: 'Passing it to your Beatly team',
};
const uid = () => Math.random().toString(36).slice(2, 10);

const fromStored = (m: LykkeMessage): ChatItem | null => {
  if (!m.text || m.role === 'event') return null;
  const meta = (m.meta || {}) as { from?: string; files?: string[] };
  return { id: m.id, role: m.role === 'user' ? 'user' : 'assistant', text: m.text, fromBeatly: meta.from === 'beatly', files: Array.isArray(meta.files) ? meta.files : undefined };
};

type SpeechRec = { continuous: boolean; interimResults: boolean; lang: string; start: () => void; stop: () => void; abort: () => void; onresult: ((e: any) => void) | null; onend: (() => void) | null; onerror: ((e: any) => void) | null };
const speechCtor = (): (new () => SpeechRec) | null => {
  if (typeof window === 'undefined') return null;
  const w = window as unknown as { SpeechRecognition?: new () => SpeechRec; webkitSpeechRecognition?: new () => SpeechRec };
  return w.SpeechRecognition || w.webkitSpeechRecognition || null;
};

export function LykkeProvider({ enabled, viewer, children, getHere, canOrder = false }: {
  enabled: boolean; viewer: Viewer; children: ReactNode;
  /** Where the person is (page + object), read at each ask. Without it Lykke gets the path only. */
  getHere?: () => LykkeHere | null;
  /** Ordering is open for this person: a brief sent with "order this" goes to Order a campaign, which reads it. */
  canOrder?: boolean;
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const qc = useQueryClient();
  const [state, setState] = useState<Record<Scope, { items: ChatItem[]; loaded: boolean; busy: boolean; working: string | null; title?: string | null }>>({});
  const [dock, setDock] = useState<{ open: boolean; scope: Scope }>({ open: false, scope: HOME });
  const [voice, setVoice] = useState<VoiceState>('off');
  const [voiceScope, setVoiceScope] = useState<Scope | null>(null);
  const [muted, setMutedState] = useState(false);
  const [caption, setCaption] = useState('');
  const earsRef = useRef<RealtimeEars | null>(null);
  const hereRef = useRef(getHere); hereRef.current = getHere;
  const pages = useMemo(() => lykkePagesFor(viewer), [viewer]);
  const pathRef = useRef(location.pathname);
  pathRef.current = location.pathname;
  const recRef = useRef<SpeechRec | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const voiceRef = useRef<{ scope: Scope | null; on: boolean }>({ scope: null, on: false });

  const patch = useCallback((scope: Scope, fn: (s: { items: ChatItem[]; loaded: boolean; busy: boolean; working: string | null; title?: string | null }) => Partial<{ items: ChatItem[]; loaded: boolean; busy: boolean; working: string | null; title: string | null }>) => {
    setState((all) => { const cur = all[scope] || { items: [], loaded: false, busy: false, working: null }; return { ...all, [scope]: { ...cur, ...fn(cur) } }; });
  }, []);

  const load = useCallback(async (scope: Scope) => {
    if (!enabled) return;
    try {
      const r = await lykkeApi.thread(scope === HOME ? null : scope);
      patch(scope, (s) => (s.busy ? { title: r.thread.title || null } : { items: r.messages.map(fromStored).filter((x): x is ChatItem => !!x), loaded: true, title: r.thread.title || null }));
    } catch {
      patch(scope, () => ({ loaded: true }));
    }
  }, [enabled, patch]);

  const speak = useCallback(async (line: string) => {
    if (!line || !voiceRef.current.on) return;
    try {
      const blob = await lykkeApi.say(line);
      if (!blob || !voiceRef.current.on) return;
      setVoice('speaking');
      setCaption(line);
      recRef.current?.abort();
      earsRef.current?.setMuted(true); // her own voice is not the person's
      const url = URL.createObjectURL(blob);
      const a = new Audio(url);
      audioRef.current = a;
      await new Promise<void>((done) => { a.onended = () => done(); a.onerror = () => done(); a.onpause = () => done(); void a.play().catch(() => done()); });
      URL.revokeObjectURL(url);
      earsRef.current?.setMuted(false);
      setCaption('');
    } catch { /* no voice: the line is in the chat */ }
  }, []);

  const note = useCallback((scope: Scope, text: string) => patch(scope, (s) => ({ items: [...s.items, { id: uid(), role: 'assistant', note: true, text }] })), [patch]);

  const send = useCallback(async (scope: Scope, text: string, via?: 'voice', files: LykkeFileIn[] = [], raw: File[] = []) => {
    const t = text.trim();
    if ((!t && !files.length) || !enabled) return;
    const replyId = uid();
    patch(scope, (s) => ({ busy: true, working: null, items: [...s.items, { id: uid(), role: 'user', text: t, via, files: files.length ? files.map((f) => f.name) : undefined }, { id: replyId, role: 'assistant', text: '', pending: true }] }));
    let spoken = '';
    const startedOn = pathRef.current;
    const set = (fn: (it: ChatItem) => ChatItem) => patch(scope, (s) => ({ items: s.items.map((it) => (it.id === replyId ? fn(it) : it)) }));
    const insertBefore = (item: ChatItem) => patch(scope, (s) => { const at = s.items.findIndex((it) => it.id === replyId); const items = s.items.slice(); items.splice(at < 0 ? items.length : at, 0, item); return { items }; });
    const here = hereRef.current?.() || null;
    await lykkeTurn({ text: t, files: files.length ? files : undefined, campaignId: scope === HOME ? null : scope, mode: via === 'voice' ? 'voice' : 'chat', pages, here: here ? { page: here.page, label: here.label, path: here.path || startedOn } : { page: 'here', path: startedOn } }, (e) => {
      if (e.event === 'working') patch(scope, () => ({ working: WORKING[e.data.tool] || 'Working on it' }));
      else if (e.event === 'navigate') {
        // "Make a deck about X": the Deck studio opens with the ask and its own agent starts building.
        const prompt = e.data.page === 'reports' ? e.data.params?.prompt : undefined;
        // A brief sent with "order this": Order a campaign opens and reads the same files into the order.
        const briefs = e.data.page === 'campaign-new' && canOrder ? raw.filter(isBriefFile) : [];
        if (briefs.length) giveHandoff('kickoff', { text: t, files: briefs });
        const to = prompt ? pathFor('reports', {}, { prompt: deckAsk(t, prompt) }) : lykkePath(e.data.page, e.data.params, e.data.focus || {});
        const label = pages.find((p) => p.id === e.data.page)?.label || 'the page';
        insertBefore({ id: uid(), role: 'nav', text: `Opened ${label}` });
        if (to) {
          navigate(to);
          // Leaving Home: the reply follows into the dock on the new page.
          if (to !== '/' && to.split('?')[0] !== pathRef.current) setDock({ open: true, scope });
        }
      } else if (e.event === 'decision') set((it) => ({ ...it, decision: e.data }));
      else if (e.event === 'request') set((it) => ({ ...it, request: e.data }));
      else if (e.event === 'say') { spoken = e.data.spoken; set((it) => ({ ...it, text: e.data.text, pending: false })); }
      else if (e.event === 'error') set((it) => ({ ...it, role: 'error', text: e.data.message, pending: false }));
    });
    patch(scope, (s) => ({ busy: false, working: null, items: s.items.filter((it) => !(it.id === replyId && it.pending && !it.text && !it.decision && !it.request)).map((it) => (it.id === replyId ? { ...it, pending: false } : it)) }));
    void qc.invalidateQueries({ queryKey: ['lykke'] });
    if (via === 'voice') { setVoice('speaking'); await speak(spoken); if (voiceRef.current.on) { setVoice('listening'); if (!earsRef.current) { try { recRef.current?.start(); } catch { /* already listening */ } } } }
  }, [canOrder, enabled, navigate, pages, patch, qc, speak]);

  const stopVoice = useCallback(() => {
    voiceRef.current = { scope: null, on: false };
    try { recRef.current?.abort(); } catch { /* not running */ }
    recRef.current = null;
    const ears = earsRef.current; earsRef.current = null; ears?.end();
    audioRef.current?.pause();
    setVoice('off');
    setVoiceScope(null);
    setMutedState(false);
    setCaption('');
  }, []);

  const setMuted = useCallback((m: boolean) => { setMutedState(m); earsRef.current?.setMuted(m); try { if (m) recRef.current?.abort(); } catch { /* not running */ } }, []);

  /** The browser's own recogniser (where Realtime is not open to this login): one utterance per turn. */
  const startBrowserVoice = useCallback((scope: Scope) => {
    const Rec = speechCtor();
    if (!Rec) { stopVoice(); return; }
    const rec = new Rec();
    rec.continuous = false;
    rec.interimResults = false;
    rec.lang = navigator.language || 'en-GB';
    rec.onresult = (e: any) => {
      const said = Array.from(e.results || []).map((r: any) => r?.[0]?.transcript || '').join(' ').trim();
      if (said) { setVoice('thinking'); void send(scope, said, 'voice'); }
    };
    // Between turns the recogniser stops by itself; it starts again after Lykke has answered (send → speak).
    rec.onend = () => { if (voiceRef.current.on) setVoice((v) => (v === 'listening' ? 'thinking' : v)); };
    rec.onerror = (e: any) => { if (e?.error === 'not-allowed' || e?.error === 'service-not-allowed') stopVoice(); };
    recRef.current = rec;
    voiceRef.current = { scope, on: true };
    setVoiceScope(scope);
    setVoice('listening');
    try { rec.start(); } catch { stopVoice(); }
  }, [send, stopVoice]);

  /** A call: OpenAI Realtime hears the person (where this login may use it, the same ears as the guide), else the browser. */
  const startVoice = useCallback((scope: Scope) => {
    stopVoice();
    voiceRef.current = { scope, on: true };
    setVoiceScope(scope);
    setVoice('connecting');
    void (async () => {
      try {
        if (typeof RTCPeerConnection === 'undefined') throw new Error('no WebRTC');
        const ears = new RealtimeEars({
          onUtterance: (said, final) => { if (!final) { setCaption(said); return; } if (said.trim().length > 1 && voiceRef.current.on) { setCaption(''); setVoice('thinking'); void send(scope, said, 'voice'); } },
          onSpeechStart: () => { if (audioRef.current && !audioRef.current.paused) audioRef.current.pause(); },
          onEnded: () => { if (earsRef.current === ears) stopVoice(); },
        });
        earsRef.current = ears;
        // The offer goes to the mint route: the server connects the ears and keeps their voice session (voiceSession.ts).
        await ears.connect((sdp) => coworkApi.calls.earsSecret({ sdp }));
        if (!voiceRef.current.on || !ears.active) { ears.end(); return; }
        setVoice('listening');
      } catch {
        earsRef.current = null;
        if (voiceRef.current.on) startBrowserVoice(scope);
      }
    })();
  }, [send, startBrowserVoice, stopVoice]);

  // A finished turn that heard nothing: listen again.
  useEffect(() => {
    if (voice !== 'thinking' || !voiceRef.current.on) return;
    const busy = voiceRef.current.scope ? state[voiceRef.current.scope]?.busy : false;
    if (busy) return;
    const t = setTimeout(() => { if (voiceRef.current.on && !state[voiceRef.current.scope || HOME]?.busy) { setVoice('listening'); if (!earsRef.current) { try { recRef.current?.start(); } catch { /* listening */ } } } }, 600);
    return () => clearTimeout(t);
  }, [voice, state]);
  useEffect(() => () => stopVoice(), [stopVoice]);

  // Stable and idempotent: effects may call them on every render without looping.
  const openDock = useCallback((scope: Scope) => setDock((d) => (d.open && d.scope === scope ? d : { open: true, scope })), []);
  const closeDock = useCallback(() => setDock((d) => (d.open ? { ...d, open: false } : d)), []);

  const value = useMemo<LykkeCtx>(() => ({
    available: enabled,
    items: (s) => state[s]?.items || [],
    loaded: (s) => !!state[s]?.loaded,
    busy: (s) => !!state[s]?.busy,
    working: (s) => state[s]?.working || null,
    title: (s) => state[s]?.title || null,
    load, send, dock,
    openDock, closeDock,
    voice, voiceScope, voiceSupported: !!speechCtor() || typeof RTCPeerConnection !== 'undefined', startVoice, stopVoice,
    muted, setMuted, caption, note,
  }), [enabled, state, load, send, dock, openDock, closeDock, voice, voiceScope, startVoice, stopVoice, muted, setMuted, caption, note]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}
